import React from 'react';
import { aiKeyApiService } from '../services/AIKeyApiService';
import {
  DEFAULT_PROVIDERS_STATUS,
  type AIProviderType,
  type AIProvidersStatus,
} from '../types/aiKey.types';
import {
  EMPTY_AVAILABLE_AI_MODELS,
  type AvailableAIModels,
  type ProviderModelAvailability,
} from '../types/aiModel.types';

interface AvailabilityCacheEntry {
  models: AvailableAIModels;
  projectProviders: AIProvidersStatus;
}

interface AvailabilityLoadResult {
  entry: AvailabilityCacheEntry;
  error: Error | null;
}

const EMPTY_CACHE_ENTRY: AvailabilityCacheEntry = {
  models: EMPTY_AVAILABLE_AI_MODELS,
  projectProviders: DEFAULT_PROVIDERS_STATUS,
};

const availabilityCache = new Map<string, AvailabilityCacheEntry>();
const availabilityRequests = new Map<string, Promise<AvailabilityLoadResult>>();

export function clearAvailableAIModelsCache(projectId?: string): void {
  if (projectId) {
    availabilityCache.delete(projectId);
    return;
  }
  availabilityCache.clear();
}

export interface UseAvailableAIModelsResult extends AvailableAIModels {
  projectProviders: AIProvidersStatus;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
  hasProvider: (
    provider: AIProviderType,
    capability?: 'textModels' | 'imageModels'
  ) => boolean;
}

export function useAvailableAIModels(projectId?: string): UseAvailableAIModelsResult {
  const [data, setData] = React.useState<AvailableAIModels>(() =>
    projectId ? availabilityCache.get(projectId)?.models ?? EMPTY_AVAILABLE_AI_MODELS : EMPTY_AVAILABLE_AI_MODELS
  );
  const [projectProviders, setProjectProviders] = React.useState<AIProvidersStatus>(() =>
    projectId ? availabilityCache.get(projectId)?.projectProviders ?? DEFAULT_PROVIDERS_STATUS : DEFAULT_PROVIDERS_STATUS
  );
  const [loading, setLoading] = React.useState(() =>
    Boolean(projectId && !availabilityCache.has(projectId))
  );
  const [error, setError] = React.useState<Error | null>(null);

  const reload = React.useCallback(async () => {
    if (!projectId) {
      setData(EMPTY_AVAILABLE_AI_MODELS);
      setProjectProviders(DEFAULT_PROVIDERS_STATUS);
      setLoading(false);
      setError(null);
      return;
    }

    const cached = availabilityCache.get(projectId);
    if (cached) {
      setData(cached.models);
      setProjectProviders(cached.projectProviders);
    }

    setLoading(true);
    setError(null);
    try {
      const { entry, error: loadError } = await loadAvailability(projectId, cached);
      setData(entry.models);
      setProjectProviders(entry.projectProviders);
      setError(loadError);
    } catch (err) {
      setError(err as Error);
      setData(cached?.models ?? EMPTY_AVAILABLE_AI_MODELS);
      setProjectProviders(cached?.projectProviders ?? DEFAULT_PROVIDERS_STATUS);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const hasProvider = React.useCallback(
    (provider: AIProviderType, capability?: 'textModels' | 'imageModels') => {
      const availability: ProviderModelAvailability | undefined = data.providers[provider];
      if (!availability?.configured) return false;
      if (!capability) return true;
      return Boolean(availability[capability]);
    },
    [data.providers]
  );

  return {
    ...data,
    projectProviders,
    loading,
    error,
    reload,
    hasProvider,
  };
}

async function loadAvailability(
  projectId: string,
  cached?: AvailabilityCacheEntry,
): Promise<AvailabilityLoadResult> {
  const inflight = availabilityRequests.get(projectId);
  if (inflight) return inflight;

  const request = fetchAvailability(projectId, cached).finally(() => {
    availabilityRequests.delete(projectId);
  });
  availabilityRequests.set(projectId, request);
  return request;
}

async function fetchAvailability(
  projectId: string,
  cached?: AvailabilityCacheEntry,
): Promise<AvailabilityLoadResult> {
  const [modelsResult, keysResult] = await Promise.allSettled([
    aiKeyApiService.listModels(projectId),
    aiKeyApiService.listKeys(projectId),
  ]);

  const models =
    modelsResult.status === 'fulfilled'
      ? modelsResult.value
      : cached?.models ?? EMPTY_AVAILABLE_AI_MODELS;
  const projectProviders =
    keysResult.status === 'fulfilled'
      ? keysResult.value
      : cached?.projectProviders ?? DEFAULT_PROVIDERS_STATUS;

  const entry = { models, projectProviders };
  if (modelsResult.status === 'fulfilled' || keysResult.status === 'fulfilled') {
    availabilityCache.set(projectId, entry);
  }

  const error =
    modelsResult.status === 'rejected'
      ? modelsResult.reason as Error
      : keysResult.status === 'rejected'
        ? keysResult.reason as Error
        : null;

  if (modelsResult.status === 'rejected' && keysResult.status === 'rejected' && !cached) {
    throw error ?? new Error('Failed to load AI model availability');
  }

  return { entry, error };
}
